/**
 * Executive Alerts Panel (MA-ALERTS-ENGINE-008 + MA-PROFIT-WEIGHT-ALERTS-009)
 * 
 * Surfaces actionable performance alerts with economic impact estimates.
 * Critical -> red, Warning -> amber/orange, Positive -> green.
 * Click an alert to filter to the relevant funnel.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertTriangle, TrendingDown, TrendingUp, ShieldAlert, CheckCircle2, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';

const SEVERITY_CONFIG = {
  critical: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/25',
    text: 'text-red-400',
    badgeBg: 'bg-red-500/20 text-red-300 border-red-500/30',
    icon: ShieldAlert,
    label: 'Critical',
  },
  warning: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/25',
    text: 'text-amber-400',
    badgeBg: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    icon: AlertTriangle,
    label: 'Warning',
  },
  positive: {
    bg: 'bg-green-500/10',
    border: 'border-green-500/25',
    text: 'text-green-400',
    badgeBg: 'bg-green-500/20 text-green-300 border-green-500/30',
    icon: CheckCircle2,
    label: 'Positive',
  },
};

const METRIC_ICON = {
  qualification_rate: TrendingDown,
  abandoned: TrendingDown,
  not_qualified: TrendingDown,
  error: AlertTriangle,
  no_consent: AlertTriangle,
};

const CONFIDENCE_LABEL = {
  high: 'Real revenue data',
  medium: 'Pipeline estimates',
  low: 'Default assumptions',
};

function formatCurrency(value) {
  const abs = Math.abs(value);
  const sign = value >= 0 ? '+' : '-';
  if (abs >= 1000) {
    // Round to nearest $1,000 for large amounts
    const rounded = Math.round(abs / 1000) * 1000;
    return `${sign}$${rounded.toLocaleString()}`;
  }
  // Round to nearest $100 for small amounts
  const rounded = Math.round(abs / 100) * 100;
  return `${sign}$${rounded.toLocaleString()}`;
}

function ImpactLine({ impact, severity }) {
  if (!impact || impact.estimated_revenue_change === 0) return null;

  const isPositive = impact.estimated_revenue_change > 0;
  const formatted = formatCurrency(impact.estimated_revenue_change);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 mt-1" data-testid="alert-impact">
            <DollarSign className={cn(
              "h-3.5 w-3.5",
              isPositive ? "text-green-400" : "text-red-400"
            )} />
            <span className={cn(
              "text-sm font-bold",
              isPositive ? "text-green-400" : "text-red-400"
            )}>
              {formatted}/mo
            </span>
            {impact.confidence && (
              <Badge
                variant="outline"
                className={cn(
                  "text-[9px] px-1 py-0 ml-1",
                  impact.confidence === 'high' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                  impact.confidence === 'medium' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                  'bg-slate-500/10 text-slate-400 border-slate-500/20'
                )}
                data-testid="impact-confidence"
              >
                {impact.confidence}
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-slate-800 border-slate-700 text-slate-200 max-w-xs">
          <div className="text-xs space-y-1">
            <p className="font-medium text-slate-100">Impact calculation</p>
            <p>{impact.leads_basis.toLocaleString()} leads in period</p>
            <p>${impact.rql_used.toLocaleString()} revenue per qualified lead</p>
            <p>{Math.abs(impact.estimated_qualified_leads_change)} qualified lead change</p>
            <p className="text-slate-400 mt-1">Confidence: {CONFIDENCE_LABEL[impact.confidence] || 'Unknown'}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function AlertRow({ alert, onAlertClick }) {
  const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.warning;
  const Icon = config.icon;
  const MetricIcon = alert.severity === 'positive' ? TrendingUp : (METRIC_ICON[alert.metric] || AlertTriangle);
  const predCtx = alert.prediction_context;

  return (
    <button
      className={cn(
        "w-full flex items-start gap-3 p-3 rounded-lg border transition-all",
        "hover:brightness-110 cursor-pointer text-left",
        config.bg, config.border,
      )}
      onClick={() => onAlertClick?.(alert)}
      data-testid={`alert-${alert.type}`}
    >
      <Icon className={cn("h-5 w-5 flex-shrink-0 mt-0.5", config.text)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", config.badgeBg)}>
            {config.label}
          </Badge>
          {alert.funnel_name && (
            <span className="text-xs text-slate-200/50 truncate max-w-[200px]">
              {alert.funnel_name}
            </span>
          )}
          {alert.low_confidence && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-500/10 text-slate-400 border-slate-500/20">
              Low sample
            </Badge>
          )}
        </div>
        <p className={cn("text-sm mt-1", config.text)}>
          {alert.message}
        </p>
        <ImpactLine impact={alert.impact} severity={alert.severity} />
        {/* MA-PREDICTION-ENGINE-010: Prediction context on qualification alerts */}
        {predCtx && (
          <div className="mt-1.5 text-xs text-slate-400 space-y-0.5" data-testid="alert-prediction-context">
            {predCtx.impact_14d != null && predCtx.impact_14d !== 0 && (
              <p>
                Projected: <span className={predCtx.impact_14d < 0 ? 'text-red-400 font-medium' : 'text-emerald-400 font-medium'}>
                  {predCtx.impact_14d < 0 ? '-' : '+'}${Math.abs(predCtx.impact_14d).toLocaleString()}/month
                </span>
              </p>
            )}
            {predCtx.days_to_threshold != null && (
              <p>
                Threshold reached in <span className="text-red-400 font-medium">~{predCtx.days_to_threshold} days</span>
              </p>
            )}
          </div>
        )}
      </div>
      <MetricIcon className={cn("h-4 w-4 flex-shrink-0 mt-1 opacity-40", config.text)} />
    </button>
  );
}

export default function ExecutiveAlerts({ alerts = [], prediction, onAlertClick }) {
  if (!alerts || alerts.length === 0) {
    return (
      <Card className="glass border-white/10" data-testid="executive-alerts-panel">
        <CardContent className="py-4">
          <div className="flex items-center gap-3 text-slate-200/50">
            <CheckCircle2 className="h-5 w-5 text-green-500/60" />
            <div>
              <p className="text-sm font-medium text-slate-200/70">No alerts</p>
              <p className="text-xs text-slate-200/40">All funnels performing within normal range</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;
  const positiveCount = alerts.filter(a => a.severity === 'positive').length;

  // Total negative impact
  const totalNegativeImpact = alerts
    .filter(a => a.impact && a.impact.estimated_revenue_change < 0)
    .reduce((sum, a) => sum + a.impact.estimated_revenue_change, 0);

  return (
    <Card className="glass border-white/10" data-testid="executive-alerts-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <ShieldAlert className={cn(
              "w-5 h-5",
              criticalCount > 0 ? "text-red-400" : warningCount > 0 ? "text-amber-400" : "text-green-400"
            )} />
            Executive Alerts
          </CardTitle>
          <div className="flex items-center gap-2">
            {totalNegativeImpact < 0 && (
              <Badge className="bg-red-500/15 text-red-300 border-red-500/20 text-xs font-bold" data-testid="total-impact-badge">
                {formatCurrency(totalNegativeImpact)}/mo at risk
              </Badge>
            )}
            {criticalCount > 0 && (
              <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-xs" data-testid="alert-count-critical">
                {criticalCount} critical
              </Badge>
            )}
            {warningCount > 0 && (
              <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs" data-testid="alert-count-warning">
                {warningCount} warning
              </Badge>
            )}
            {positiveCount > 0 && (
              <Badge className="bg-green-500/20 text-green-300 border-green-500/30 text-xs" data-testid="alert-count-positive">
                {positiveCount} positive
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {alerts.map((alert, idx) => (
            <AlertRow key={`${alert.type}-${idx}`} alert={alert} onAlertClick={onAlertClick} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
